import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { finalize } from 'rxjs';
import { ThemeService } from 'src/app/core/services/theme.service';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import { TicketAnalysisFilters, TicketAnalysisPage, VarianceSummary } from '../../../models/ticket-analysis.models';
import { TicketAnalysisService } from '../../../services/ticket-analysis.service';
import { ViewPersistenceService } from 'src/app/features/lifecycle-command/services/view-persistence.service';
import {
  TICKET_ANALYSIS_FILTERS_LAYOUT,
  applyFilterDropdowns,
  createTicketAnalysisFilterConfigMap,
  createTicketAnalysisFiltersForm,
  defaultDateFrom,
  extractBaseFilters,
} from '../ticket-analysis-filters.utils';
import {
  BAR_CATEGORY_PERCENTAGE,
  BAR_PERCENTAGE,
  VARIANCE_CLOSED_COLOR_DARK,
  VARIANCE_CLOSED_COLOR_LIGHT,
  VARIANCE_ENTERED_COLOR,
  VARIANCE_LINE_COLOR_ENTERED,
  VARIANCE_YEAR_LABEL_COLOR,
} from '../ticket-analysis-chart.constants';

@Component({
  selector: 'ticket-analysis-variance',
  templateUrl: './ticket-analysis-variance.component.html',
  styleUrls: ['./ticket-analysis-variance.component.scss'],
  standalone: false,
})
export class TicketAnalysisVarianceComponent implements OnInit, OnDestroy {
  loading = false;
  loadingTickets = false;
  error = '';
  summary: VarianceSummary | null = null;
  ticketPageData: TicketAnalysisPage | null = null;

  currentPage = 1;
  pageSize = 25;
  readonly displayedColumns = ['id', 'dateEntered', 'closedDate', 'contact', 'summary'];

  monthlyChartData: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  // Mixed bar+line charts; Chart.js doesn't have a single generic for mixed types
  hourEnteredChartData: any = { labels: [], datasets: [] };
  hourClosedChartData: any = { labels: [], datasets: [] };

  groupedBarOptions: ChartConfiguration<'bar'>['options'] = {};
  hourEnteredBarOptions: ChartConfiguration<'bar'>['options'] = {};
  hourClosedBarOptions: ChartConfiguration<'bar'>['options'] = {};

  private labelColor = '#555555';
  private closedBarColor = VARIANCE_CLOSED_COLOR_LIGHT;
  private yearGroups: { year: string; start: number; end: number }[] = [];

  readonly monthlyChartPlugins: Plugin[] = [
    {
      id: 'barTopLabels',
      afterDatasetsDraw: (chart: Chart) => {
        if ((chart.data.labels?.length ?? 0) === 24) return;
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset, i) => {
          const meta = chart.getDatasetMeta(i);
          if ((meta as any).type !== 'bar' || meta.hidden) return;
          meta.data.forEach((el, j) => {
            const val = dataset.data[j];
            if (typeof val !== 'number') return;
            const { x, y } = (el as any).getProps(['x', 'y'], true);
            ctx.save();
            ctx.translate(x, y - 10);
            ctx.rotate(-Math.PI / 2);
            ctx.font = '400 12px Inter, sans-serif';
            ctx.fillStyle = this.labelColor;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(val), 0, 0);
            ctx.restore();
          });
        });
      },
    },
    {
      id: 'yearGroupLabels',
      afterDraw: (chart: Chart) => {
        if (!this.yearGroups.length || (chart.data.labels?.length ?? 0) === 24) return;
        const { ctx, scales, chartArea } = chart;
        const xAxis = scales['x'];
        if (!xAxis || !chartArea) return;

        ctx.save();
        ctx.font = 'italic 600 14px Inter, sans-serif';
        ctx.fillStyle = VARIANCE_YEAR_LABEL_COLOR;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        for (const { year, start, end } of this.yearGroups) {
          const cx = (xAxis.getPixelForTick(start) + xAxis.getPixelForTick(end)) / 2;
          ctx.fillText(year, cx, chartArea.bottom + 36);
        }
        ctx.restore();

        for (let i = 1; i < this.yearGroups.length; i++) {
          const sepX =
            (xAxis.getPixelForTick(this.yearGroups[i].start - 1) + xAxis.getPixelForTick(this.yearGroups[i].start)) / 2;
          ctx.save();
          ctx.setLineDash([4, 4]);
          ctx.strokeStyle = this.labelColor + '55';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(sepX, chartArea.top - 30);
          ctx.lineTo(sepX, chartArea.bottom + 30);
          ctx.stroke();
          ctx.restore();
        }
      },
    },
  ];

  readonly hourlyChartPlugins: Plugin[] = [
    {
      id: 'hourlyBarLabels',
      afterDatasetsDraw: (chart: Chart) => {
        if ((chart.data.labels?.length ?? 0) !== 24) return;
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset, i) => {
          const meta = chart.getDatasetMeta(i);
          if ((meta as any).type !== 'bar' || meta.hidden) return;
          meta.data.forEach((el, j) => {
            const val = dataset.data[j];
            if (typeof val !== 'number' || val === 0) return;
            const { x, y } = (el as any).getProps(['x', 'y'], true);
            ctx.save();
            ctx.font = '9px Inter, sans-serif';
            ctx.fillStyle = this.labelColor;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(String(val), x, y - 2);
            ctx.restore();
          });
        });
      },
    },
  ];

  filtersForm = createTicketAnalysisFiltersForm();
  valuesMap: any = this.filtersForm.value;
  filtersConfigMap: Map<string, IFormControlConfig> = createTicketAnalysisFilterConfigMap();
  readonly filtersLayoutConfig = TICKET_ANALYSIS_FILTERS_LAYOUT;

  private readonly STORAGE_KEY = 'ticket_analysis_variance';

  constructor(
    private readonly ticketAnalysisService: TicketAnalysisService,
    private readonly themeService: ThemeService,
    private readonly destroyRef: DestroyRef,
    private viewPersistenceService: ViewPersistenceService,
  ) {}

  ngOnDestroy(): void {
    Chart.unregister(...this.monthlyChartPlugins, ...this.hourlyChartPlugins);
  }

  ngOnInit(): void {
    this.buildChartOptions();
    this.themeService.themeSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildChartOptions();
      if (this.summary) this.buildAllCharts(this.summary);
    });
    this.restoreFilters();
    this.ticketAnalysisService.exportRequested$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.ticketAnalysisService.startExport(this.buildFilters(), { view: 'variance' }));
    this.loadDropdowns();
    this.load();
  }

  updateValuesMap(valuesMap: any): void {
    this.valuesMap = valuesMap;
  }

  applyFilters(): void {
    this.saveFilters();
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      dateRange: { start: defaultDateFrom(), end: new Date() },
      site: null,
      board: null,
      contactName: null,
      status: null,
      source: null,
    });
    this.valuesMap = this.filtersForm.value;
    this.viewPersistenceService.clearFilters(this.STORAGE_KEY);
    this.load();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadTickets();
  }

  loadTickets(): void {
    this.loadingTickets = true;
    this.ticketAnalysisService
      .getTickets(this.buildFilters(), this.currentPage, this.pageSize)
      .pipe(finalize(() => (this.loadingTickets = false)))
      .subscribe({ next: (data) => (this.ticketPageData = data) });
  }

  private load(): void {
    this.currentPage = 1;
    this.loading = true;
    this.error = '';
    this.ticketAnalysisService
      .getVariance(this.buildFilters())
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data) => {
          this.summary = data;
          this.buildAllCharts(data);
        },
        error: () => (this.error = 'Failed to load variance data.'),
      });
    this.loadTickets();
  }

  private loadDropdowns(): void {
    this.ticketAnalysisService.getFilters().subscribe({
      next: (response) => {
        this.filtersConfigMap = applyFilterDropdowns(response, this.filtersConfigMap);
      },
    });
  }

  private buildFilters(): TicketAnalysisFilters {
    return extractBaseFilters(this.valuesMap);
  }

  private buildAllCharts(data: VarianceSummary): void {
    const yearGroupMap = new Map<string, { start: number; end: number }>();
    const monthLabels = data.byMonth.map(({ month }, index) => {
      const [year, m] = month.split('-');
      if (!yearGroupMap.has(year)) {
        yearGroupMap.set(year, { start: index, end: index });
      } else {
        yearGroupMap.get(year)!.end = index;
      }
      return new Date(+year, +m - 1).toLocaleString('en-US', { month: 'short' }).toUpperCase();
    });
    this.yearGroups = Array.from(yearGroupMap.entries()).map(([year, range]) => ({ year, ...range }));

    const barDefaults = { barPercentage: BAR_PERCENTAGE, categoryPercentage: BAR_CATEGORY_PERCENTAGE };
    this.monthlyChartData = {
      labels: monthLabels,
      datasets: [
        {
          label: 'Entered',
          data: data.byMonth.map((m) => m.entered),
          backgroundColor: VARIANCE_ENTERED_COLOR,
          ...barDefaults,
        },
        {
          label: 'Closed',
          data: data.byMonth.map((m) => m.closed),
          backgroundColor: this.closedBarColor,
          ...barDefaults,
        },
      ],
    };

    const hourLabels = Array.from({ length: 24 }, (_, i) => String(i));
    const avgEntered = data.byHourEntered.reduce((s, h) => s + h.count, 0) / 24;
    const avgClosed = data.byHourClosed.reduce((s, h) => s + h.count, 0) / 24;

    const lineDataset = (avg: number, color: string) => ({
      type: 'line' as const,
      label: 'Average Number of Tickets',
      data: Array(24).fill(Math.round(avg * 10) / 10),
      borderColor: color,
      backgroundColor: 'transparent',
      pointBackgroundColor: color,
      pointRadius: 3,
      borderWidth: 2,
      tension: 0.3,
      order: 1,
    });

    this.hourEnteredChartData = {
      labels: hourLabels,
      datasets: [
        {
          type: 'bar',
          label: 'Number of Tickets',
          data: data.byHourEntered.map((h) => h.count),
          backgroundColor: VARIANCE_ENTERED_COLOR,
          order: 2,
        },
        lineDataset(avgEntered, VARIANCE_LINE_COLOR_ENTERED),
      ],
    };

    this.hourClosedChartData = {
      labels: hourLabels,
      datasets: [
        {
          type: 'bar',
          label: 'Number of Tickets',
          data: data.byHourClosed.map((h) => h.count),
          backgroundColor: this.closedBarColor,
          order: 2,
        },
        lineDataset(avgClosed, VARIANCE_ENTERED_COLOR),
      ],
    };
  }

  private buildChartOptions(): void {
    const isDark = this.themeService.isInDarkMode();
    this.labelColor = isDark ? '#F1F1F1' : '#555555';
    this.closedBarColor = isDark ? VARIANCE_CLOSED_COLOR_DARK : VARIANCE_CLOSED_COLOR_LIGHT;
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    const labelColor = this.labelColor;

    const base: ChartConfiguration<'bar'>['options'] = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: labelColor, boxWidth: 12, font: { size: 11 } } },
        tooltip: { enabled: true },
      },
      scales: {
        x: { ticks: { color: labelColor }, grid: { color: gridColor } },
        y: { ticks: { color: labelColor }, grid: { color: gridColor }, beginAtZero: true },
      },
    };

    this.groupedBarOptions = {
      ...base,
      layout: { padding: { top: 44, bottom: 36 } },
      plugins: { ...base.plugins, legend: { display: false } },
    };

    const hourScales = {
      ...base.scales,
      x: {
        ticks: { color: labelColor, font: { size: 9 }, autoSkip: false, maxRotation: 0 },
        grid: { color: gridColor },
        title: { display: true, color: labelColor, font: { size: 10, weight: '700' as const }, padding: { top: 6 } },
      },
      y: { ticks: { color: labelColor }, grid: { color: gridColor }, beginAtZero: true },
    };

    const hourPlugins = {
      ...base.plugins,
      title: { display: true, color: labelColor, font: { size: 13, weight: '600' as const }, padding: { bottom: 4 } },
      legend: {
        position: 'top' as const,
        align: 'center' as const,
        labels: {
          color: labelColor,
          pointStyle: 'circle',
          usePointStyle: true,
          boxWidth: 8,
          boxHeight: 8,
          font: { size: 10 },
          padding: 16,
        },
      },
    };

    this.hourEnteredBarOptions = {
      ...base,
      layout: { padding: { top: 14 } },
      plugins: { ...hourPlugins, title: { ...hourPlugins.title, text: 'Ticket Hour Entered' } },
      scales: { ...hourScales, x: { ...hourScales.x, title: { ...hourScales.x.title, text: 'Hour of Date Entered' } } },
    };

    this.hourClosedBarOptions = {
      ...base,
      layout: { padding: { top: 14 } },
      plugins: { ...hourPlugins, title: { ...hourPlugins.title, text: 'Ticket Hour Closed' } },
      scales: { ...hourScales, x: { ...hourScales.x, title: { ...hourScales.x.title, text: 'Hour of Date Closed' } } },
    };
  }

  private saveFilters(): void {
    const dateRange = this.valuesMap?.dateRange as { start: Date; end: Date } | null;
    this.viewPersistenceService.saveFilters(this.STORAGE_KEY, {
      ...this.valuesMap,
      dateRange: dateRange
        ? { start: dateRange.start?.toISOString?.() ?? null, end: dateRange.end?.toISOString?.() ?? null }
        : null,
    });
  }

  private restoreFilters(): void {
    const saved = this.viewPersistenceService.loadFilters(this.STORAGE_KEY);
    if (!saved) return;
    const rawRange = saved['dateRange'] as { start: string; end: string } | null;
    const start = rawRange?.start ? new Date(rawRange.start) : defaultDateFrom();
    const end = rawRange?.end ? new Date(rawRange.end) : new Date();
    const asTree = (v: unknown) =>
      v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : null;
    const asBoard = (v: unknown) => (Array.isArray(v) && v.length > 0 ? v : null);
    const restored = {
      dateRange: { start, end },
      contactName: (saved['contactName'] as string | null) ?? null,
      board: asBoard(saved['board']),
      site: asTree(saved['site']),
      status: asTree(saved['status']),
      source: asTree(saved['source']),
    };
    this.filtersForm.patchValue(restored);
    this.valuesMap = restored;
  }
}
